import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';
import { getServerDate, ServerDateResult } from './getServerDate';

export class ServerDateService {
  private static readonly SERVER_DATE_TIMEOUT = 10_000;
  private static readonly DEFAULT_DATE_FACTORY = () => new Date();

  private lifecycle: 'created' | 'initializing' | 'ready.serverDate' | 'ready.localDate' = 'created';

  private dateFactory: () => Date = ServerDateService.DEFAULT_DATE_FACTORY;
  private loadingPromise: Promise<void> | null = null;
  private lastSuccessfulServerDate: ServerDateResult | null = null;

  async syncServerDate() {
    if (this.lifecycle === 'ready.serverDate') {
      return this;
    }

    if (!this.loadingPromise) {
      this.loadingPromise = this.setupServerDate().finally(() => {
        this.loadingPromise = null;
      });
    }

    await this.loadingPromise;
    return this;
  }

  private async setupServerDate() {
    this.lifecycle = 'initializing';

    try {
      const serverDatePromise = this.lastSuccessfulServerDate
        ? Promise.resolve(this.lastSuccessfulServerDate)
        : getServerDate().then((result) => {
            // in case this is cancelled by the timeout,
            // we don't want to lose the last successful server date.
            this.lastSuccessfulServerDate = result;
            return result;
          });

      const serverDateConfig = await Promise.race([
        serverDatePromise,
        waitFor(ServerDateService.SERVER_DATE_TIMEOUT).then(() => {
          throw new Error('Timeout fetching server date');
        }),
      ]);

      this.lastSuccessfulServerDate = serverDateConfig;
      this.dateFactory = () => serverDateConfig.utcNow();
      this.lifecycle = 'ready.serverDate';
    } catch (error) {
      logger.error('[SERVER_DATE_SERVICE] Failed to fetch server date:', { error: parseErrorMessage(error) });

      if (this.lastSuccessfulServerDate) {
        this.dateFactory = () => this.lastSuccessfulServerDate!.utcNow();
        this.lifecycle = 'ready.serverDate';
        return;
      }

      this.dateFactory = ServerDateService.DEFAULT_DATE_FACTORY;
      this.lifecycle = 'ready.localDate';
    }
  }

  date(): Date {
    return this.dateFactory();
  }

  async getDateAsync(): Promise<Date> {
    if (this.lifecycle === 'ready.serverDate') {
      return this.date();
    }

    await this.syncServerDate();
    return this.date();
  }
}

// Local version of the function for easy copying across projects.
function waitFor(timeout: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, timeout));
}
